import { Component, ElementRef, ViewChild, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { DashboardComponent } from '../../view/dashboard/dashboard.component';
import { AlbaranesComponent } from '../main/3_ventas/albaranes/albaranes.component';
import { LoginApiService } from '../../service/api/login-api.service';
import { StorageManager } from '../../common/storage-manager.class';
import { environment } from '../../../environments/environment';
import { UserModel } from '../../models/usuarios/user.model';
import type { Value } from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';

type ValuesOpt = Value<UserModel>;

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit {
  @ViewChild('passport', { static: true }) passport!: ElementRef;

  env = environment.appName;
  
  logoUrl = environment.logoUrl;
  
  serverUrl = environment.serverUrl;
  
  canRegister = environment.features.canRegister;
  
  canRecoverPassword = environment.features.canRecoverPassword;
  
  urlLogoLocatec = environment.serverUrl + 'assets/logo-locatec-footer.png';
  
  showWatermark = environment.features.showWatermark;
  
  hasUsuarios = environment.features.hasUsuarios;

  // UI state
  error = false;
  
  success = false;
  
  isDoLoginLoading = false;
  
  showPassword = false;

  status: string | null = null;

  sessionId: string | null = null;

  userId: string | null = null;

  password: string | null = null;

  // Recovery state
  isSendingEmail = false;
  
  hasSentEmail = false;
  
  isCheckingCode = false;
  
  codeHasError = false;
  
  codeHasBeenChecked = false;
  
  isSendingPassword = false;
  
  userRecovery: ValuesOpt | undefined = { label: 'Cargando...', value: {} as UserModel };

  // Users
  loginWithInputUsers = ['melia', 'manezylozano', 'sequiador', 'agroleza', 'benibal', 'democropgest', 'mqm', 'fjperan', 'agrogestSIEX'];

  userList: { selected: null | Value<UserModel>; values: (ValuesOpt | null)[] } = {
    selected: null,
    values: [{ label: 'Cargando...', value: {} as UserModel }],
  };

  // Reactive form
  form: FormGroup = this.fb.group({
    username: [''],                 // used when isDirectUserInput
    userId: [''],                   // used when dropdown
    password: ['', [Validators.required, Validators.minLength(3)]],
  });

  constructor(
    private readonly router: Router,
    private readonly loginApi: LoginApiService,
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute
  ) {
    void router;
  }

  get isDirectUserInput(): boolean {
    return this.loginWithInputUsers.includes(this.env);
  }

  /** Brand subtitle map */
  get subtitle(): string | null {
    if (this.env === 'dqvademecum') {return 'dq VADEMECUM';}
    if (this.env === 'ava' || this.env === 'avafitogest') {return 'Associació Valenciana d\'Agricultors';}
    return null;
  }

  async ngOnInit(): Promise<void> {
    // Load selectable users
    try {
      const users = await this.loginApi.getUsers();
      
      this.userList.values = [{ label: 'Seleccionar usuario...', value: null as any }];
      users
        .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
        .forEach(u => this.userList.values.push({ label: `${u.nombre || ''} ${u.apellidos || ''}`, value: u }));
    } catch (e) {
      console.log('getUsers error:', e);
    }

    // Session check
    try {
      const exists = await this.loginApi.checkCurrentSession();
      
      if (exists) {
        if (environment.appName !== 'gruesnadal') {
          DashboardComponent.createNavigation(this.router);
        } else {
          AlbaranesComponent.createNavigation(this.router);
        }
        return;
      }
    } catch (e) {
      console.log('checkCurrentSession error:', e);
    }

    // Demo user fallback
    if (!this.hasUsuarios && !StorageManager.getUser().id) {
      StorageManager.saveUser({
        id: '0',
        nombre: 'Usuario',
        apellidos: '',
        password: '',
        rol: 'demo',
        username: 'usuario',
      });
      window.location.reload();
    }

    this.route.queryParamMap.subscribe(params => {
      this.status = params.get('status');
      this.sessionId = params.get('session_id');
      this.userId    = params.get('user_id');
      this.password  = params.get('password');

      if (this.userId && this.password) {
        this.onSubmit();
      }
    });
  }

  onUserIdChange(): void {
    // Placeholder to react to userId changes if needed later
  }

  showInvalid(control: 'username' | 'userId' | 'password'): boolean {
    const c = this.form.get(control);
    
    if (!c) {return false;}
    // Only mark username/userId as required depending on mode
    if (control === 'username' && !this.isDirectUserInput) {return false;}
    if (control === 'userId' && this.isDirectUserInput) {return false;}
    return c.invalid && (c.dirty || c.touched);
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  async onSubmit(): Promise<void> {
    this.error = false;
    let userToLogin: UserModel | undefined;
    if (environment.features.netAgroErpIntegration) {
      userToLogin = {id: this.userId ?? '', password: this.password ?? ''} as UserModel;
    } else {
      // Validate mode-specific field
      if (this.isDirectUserInput) {
        if (!(Boolean((this.form.value.username?.trim())))) {
          this.form.get('username')?.setErrors({ required: true });
          return;
        }
      } else {
        if (!(Boolean(this.form.value.userId))) {
          this.form.get('userId')?.setErrors({ required: true });
          return;
        }
      }
      if ((this.form.get('password')?.invalid) ?? false) {return;}
      
      if (this.isDirectUserInput) {
        userToLogin = { id: this.form.value.username, password: this.form.value.password } as UserModel;
      } else {
        const selected = this.userList.values.find(v => v?.value?.id === this.form.value.userId)?.value;
        
        if (selected) {
          userToLogin = { ...selected, password: this.form.value.password };
        }
      }
      if (!userToLogin) {return;}
    }

    // Call API
    this.isDoLoginLoading = true;
    try {
      const isValid = await this.loginApi.doLogin(userToLogin);

      this.isDoLoginLoading = false;

      if (isValid) {
        this.success = true;
        window.location.reload();
      } else {
        this.error = true;
      }
    } catch {
      this.isDoLoginLoading = false;
      this.error = true;
    }
  }

  crearCuenta(): void {
    this.router.navigate(['register']);
  }

  recuperarContrasenya(): void {
    this.router.navigate(['password-recovery']);
  }
}
